import { useState, FC } from "react";
import { CartTransformedResponse } from "../../../types/cart_response_type";
import { useLocaleContext } from "../../context";
import {
  Badge,
  BadgeProps,
  Box,
  Collapse,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Tabs,
  Typography,
  styled,
} from "@mui/material";
import { CartItem } from "../CheckoutCart/CartItem";
import { FiChevronDown, FiChevronUp, FiShoppingCart } from "react-icons/fi";
import { ShareButton } from "../../atoms";
import { useGetWishlistQuery } from "../../../store";
import { WishlistItem } from "../Wishlist/WishListItem";
import { grey } from "@mui/material/colors";

interface Props {
  cartData: CartTransformedResponse;
  currency: "INR" | "USD" | undefined;
}

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
  "& .MuiBadge-badge": {
    right: -3,
    top: 13,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: "0 4px",
  },
}));

export const OrderSummary: FC<Props> = ({ cartData, currency }) => {
  const { data: wishlistData } = useGetWishlistQuery();
  const [activeTab, setActiveTab] = useState(0);
  const [open, setOpen] = useState(true);
  const { currency: c } = useLocaleContext();

  const getActiveCurrency = (c: string) => {
    return c === "INR" ? cartData.value[1] : cartData.value[0];
  };

  const symbol = currency === "INR" ? "₹" : "$";

  const activeCurrency = currency
    ? getActiveCurrency(currency)
    : getActiveCurrency(c);

  const shareText = `Hey! Check out these awesome products on Jumkey. Hope you will like them :)\n\n${cartData.products.reduce(
    (a, c, index) => {
      return (
        a +
        `${index + 1}. ${c.title}:\n https://jumkey.com/products/${
          c.productHandle
        }\n\n`
      );
    },
    ""
  )}
  `;

  return (
    <div>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack
          gap={1}
          direction="row"
          alignItems="center"
          onClick={() => setOpen(p => !p)}
          sx={{ cursor: "pointer" }}>
          <Typography
            variant="subtitle2"
            textTransform="uppercase"
            fontWeight={700}>
            Cart Items
          </Typography>
          <IconButton size="small">
            {open ? <FiChevronUp /> : <FiChevronDown />}
          </IconButton>
        </Stack>
        <Stack direction="row" gap={2} alignItems="center">
          <ShareButton
            numberOfItems={cartData.products.length}
            shareText={shareText}
            size="small"
          />
          <Typography variant="subtitle1" fontWeight={700}>
            {symbol}
            {activeCurrency.sellingPrice}
          </Typography>
        </Stack>
      </Stack>
      <Collapse in={open}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{ bgcolor: grey[50], mt: 2 }}>
          <Tab label={`Cart(${cartData.products.length})`} />
          <Tab label={`Wishlist(${wishlistData?.length || 0})`} />
        </Tabs>

        <Stack gap={2} sx={{ mt: 3 }}>
          {activeTab === 0 && (
            <>
              {cartData.products
                .filter(p => p.cartItemType === "valid")
                .map((p: any) => {
                  return (
                    <CartItem
                      key={p.id}
                      product={p}
                      defaultCurrency={currency}
                    />
                  );
                })}
            </>
          )}
          {activeTab === 1 && (
            <>
              {wishlistData?.map((p: any) => {
                return <WishlistItem product={p} key={p.id} />;
              })}
            </>
          )}
        </Stack>
      </Collapse>
    </div>
  );
};
